// src/pages/employer/profile/EmployerContacts.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { orgAPI } from '../../../services/api';
import { useConfirm, EditBtn, AddBtn, DelBtn } from '../../../components/shared/UI';
import { PhoneCapture } from '../../../components/shared/PhoneInput';
import { SectionModal } from '../../../components/shared/FieldModals';
import { Users, MapPin, Mail, Phone, ExternalLink, X } from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────

const newId  = () => crypto.randomUUID();
const newEmail = () => ({ id: newId(), value: '', label: 'Work' });
const newPhone = () => ({ id: newId(), code: '+61', number: '', label: 'Mobile' });
const newLink  = () => ({ id: newId(), label: '', url: '' });
const EMAIL_LABELS = ['Work', 'Personal', 'Other'];

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const subLabel = {
  fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 8px',
};

// ── Contact editor (inside modal) ─────────────────────────

const ContactEditor = ({ form, setForm, workplaces }) => {
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

  const updateEmail = (id, patch) => set('emails')(form.emails.map((e) => e.id === id ? { ...e, ...patch } : e));
  const removeEmail = (id)        => set('emails')(form.emails.filter((e) => e.id !== id));
  const updatePhone = (id, patch) => set('phones')(form.phones.map((p) => p.id === id ? { ...p, ...patch } : p));
  const removePhone = (id)        => set('phones')(form.phones.filter((p) => p.id !== id));
  const updateLink  = (id, patch) => set('links')(form.links.map((l) => l.id === id ? { ...l, ...patch } : l));
  const removeLink  = (id)        => set('links')(form.links.filter((l) => l.id !== id));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Name <span style={{ color: '#CC0000' }}>*</span></label>
          <input value={form.name} onChange={(e) => set('name')(e.target.value)}
            style={inputStyle} placeholder="Contact name" autoFocus />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Position / Role</label>
          <input value={form.position} onChange={(e) => set('position')(e.target.value)}
            style={inputStyle} placeholder="e.g. Site Manager" />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Workplace</label>
        <select value={form.workplaceId || ''} onChange={(e) => set('workplaceId')(e.target.value || null)}
          style={inputStyle}>
          <option value="">— Not associated with a specific workplace —</option>
          {workplaces.map((w) => (
            <option key={w.id} value={w.id}>{w.name}{w.isDefault ? ' (Default)' : ''}</option>
          ))}
        </select>
      </div>

      {/* Emails */}
      <div style={subLabel}>Emails</div>
      {form.emails.map((email) => (
        <div key={email.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <select value={email.label} onChange={(e) => updateEmail(email.id, { label: e.target.value })}
            style={{ ...inputStyle, width: 100, flexShrink: 0 }}>
            {EMAIL_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <input type="email" value={email.value} onChange={(e) => updateEmail(email.id, { value: e.target.value })}
            style={{ ...inputStyle, flex: 1 }} placeholder="email@example.com" />
          <DelBtn onClick={() => removeEmail(email.id)} />
        </div>
      ))}
      <AddBtn onClick={() => set('emails')([...form.emails, newEmail()])} title="Add Email" />

      {/* Phones */}
      <div style={subLabel}>Phones</div>
      {form.phones.map((phone) => (
        <div key={phone.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <PhoneCapture
              code={phone.code} number={phone.number} label={phone.label}
              onCodeChange={(v) => updatePhone(phone.id, { code: v })}
              onNumberChange={(v) => updatePhone(phone.id, { number: v })}
              onLabelChange={(v) => updatePhone(phone.id, { label: v })}
            />
          </div>
          <DelBtn onClick={() => removePhone(phone.id)} />
        </div>
      ))}
      <AddBtn onClick={() => set('phones')([...form.phones, newPhone()])} title="Add Phone" />

      {/* Links */}
      <div style={subLabel}>Links</div>
      {form.links.map((link) => (
        <div key={link.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input type="text" value={link.label} onChange={(e) => updateLink(link.id, { label: e.target.value })}
            style={{ ...inputStyle, width: 100, flexShrink: 0 }} placeholder="Label" />
          <input type="url" value={link.url} onChange={(e) => updateLink(link.id, { url: e.target.value })}
            style={{ ...inputStyle, flex: 1 }} placeholder="https://" />
          <DelBtn onClick={() => removeLink(link.id)} />
        </div>
      ))}
      <AddBtn onClick={() => set('links')([...form.links, newLink()])} title="Add Link" />
    </div>
  );
};

// ── Contact modal ──────────────────────────────────────────

const ContactModal = ({ initial, workplaces, onSave, onClose }) => {
  const [form, setForm] = useState({
    name:        initial?.name        || '',
    position:    initial?.position    || '',
    workplaceId: initial?.workplaceId || null,
    emails:      initial?.emails      || [],
    phones:      initial?.phones      || [],
    links:       initial?.links       || [],
  });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 600, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <span>{initial ? 'Edit Contact' : 'Add Contact'}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 4 }}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
          <ContactEditor form={form} setForm={setForm} workplaces={workplaces} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : 'Save Contact'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Contact card field-row styles (matches Workplaces / Overview pattern) ──

const cardRowStyle   = { display: 'flex', alignItems: 'flex-start', padding: '8px 16px' };
const cardLabelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  minWidth: 100, flexShrink: 0, paddingTop: 2, textAlign: 'right',
};
const cardValueStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 14px', flex: 1 };
const emptyVal       = { fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' };

// ── Contact card (display + per-field editing) ─────────────

const ContactCard = ({ contact, workplaceName, workplaces, isAdmin, onEdit, onDelete, onSaveField }) => {
  const [fieldEdit, setFieldEdit] = useState(null); // { field, val } | null
  const [saving,    setSaving]    = useState(false);

  const openField = (field) => {
    const val =
      field === 'name'      ? (contact.name         || '') :
      field === 'position'  ? (contact.position      || '') :
      field === 'workplace' ? (contact.workplaceId   || null) :
      field === 'emails'    ? (contact.emails  || []).map(e => ({ ...e })) :
      field === 'phones'    ? (contact.phones  || []).map(p => ({ ...p })) :
                              (contact.links   || []).map(l => ({ ...l }));
    setFieldEdit({ field, val });
  };
  const closeField = () => { setFieldEdit(null); setSaving(false); };
  const setVal     = (v) => setFieldEdit(fe => ({ ...fe, val: v }));

  const saveField = async (patch) => {
    setSaving(true);
    try { await onSaveField(contact.id, patch); closeField(); }
    finally { setSaving(false); }
  };

  // Blue pencil for body fields
  const PBtn = ({ field }) => isAdmin ? (
    <EditBtn onClick={() => openField(field)} />
  ) : null;

  return (
    <>
      <div className="section-box" style={{ marginBottom: 12 }}>
        {/* Header */}
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700 }}>{contact.name}</span>
            {isAdmin && (
              <EditBtn onClick={() => openField('name')} title="Edit full name" />
            )}
          </div>
          {isAdmin && (
            <DelBtn onClick={onDelete} title="Delete contact" />
          )}
        </div>

        {/* Body */}
        <div className="section-box-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
          {/* Position */}
          <div style={cardRowStyle}>
            <div style={cardLabelStyle}>Position:</div>
            <div style={cardValueStyle}>
              {contact.position
                ? <span style={{ fontSize: 13, color: 'var(--text)' }}>{contact.position}</span>
                : <span style={emptyVal}>Not set</span>
              }
              <PBtn field="position" />
            </div>
          </div>

          {/* Workplace */}
          <div style={cardRowStyle}>
            <div style={cardLabelStyle}>Workplace:</div>
            <div style={cardValueStyle}>
              {workplaceName ? (
                <><MapPin size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>{workplaceName}</span></>
              ) : <span style={emptyVal}>Not set</span>}
              <PBtn field="workplace" />
            </div>
          </div>

          {/* Emails */}
          <div style={{ ...cardRowStyle, alignItems: 'flex-start' }}>
            <div style={{ ...cardLabelStyle, paddingTop: 3 }}>Email:</div>
            <div style={{ ...cardValueStyle, alignItems: 'flex-start', flexDirection: 'column', gap: 5 }}>
              {contact.emails?.length > 0 ? contact.emails.map((e, idx) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Mail size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <a href={`mailto:${e.value}`} style={{ fontSize: 13, color: 'var(--text)', textDecoration: 'none' }}
                    onMouseEnter={ev => { ev.target.style.textDecoration = 'underline'; }}
                    onMouseLeave={ev => { ev.target.style.textDecoration = 'none'; }}>
                    {e.value}
                  </a>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({e.label})</span>
                  {idx === 0 && <PBtn field="emails" />}
                </div>
              )) : <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={emptyVal}>None</span><PBtn field="emails" /></div>}
            </div>
          </div>

          {/* Phones */}
          <div style={{ ...cardRowStyle, alignItems: 'flex-start' }}>
            <div style={{ ...cardLabelStyle, paddingTop: 3 }}>Phone:</div>
            <div style={{ ...cardValueStyle, alignItems: 'flex-start', flexDirection: 'column', gap: 5 }}>
              {contact.phones?.length > 0 ? contact.phones.map((p, idx) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Phone size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>{p.code} {p.number}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({p.label})</span>
                  {idx === 0 && <PBtn field="phones" />}
                </div>
              )) : <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={emptyVal}>None</span><PBtn field="phones" /></div>}
            </div>
          </div>

          {/* Links */}
          <div style={{ ...cardRowStyle, alignItems: 'flex-start' }}>
            <div style={{ ...cardLabelStyle, paddingTop: 3 }}>Links:</div>
            <div style={{ ...cardValueStyle, alignItems: 'flex-start', flexDirection: 'column', gap: 5 }}>
              {contact.links?.length > 0 ? contact.links.map((l, idx) => (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <ExternalLink size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <a href={l.url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 13, color: 'var(--secondary)', textDecoration: 'none', wordBreak: 'break-all' }}
                    onMouseEnter={ev => { ev.target.style.textDecoration = 'underline'; }}
                    onMouseLeave={ev => { ev.target.style.textDecoration = 'none'; }}>
                    {l.url}
                  </a>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({l.label})</span>
                  {idx === 0 && <PBtn field="links" />}
                </div>
              )) : <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={emptyVal}>None</span><PBtn field="links" /></div>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Per-field modals ── */}
      {fieldEdit?.field === 'name' && (
        <SectionModal title="Edit Full Name" saving={saving}
          onSave={() => saveField({ name: fieldEdit.val })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Full Name <span style={{ color: '#CC0000' }}>*</span></label>
            <input type="text" value={fieldEdit.val} onChange={e => setVal(e.target.value)}
              style={inputStyle} autoFocus placeholder="Contact full name" />
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'position' && (
        <SectionModal title="Edit Position" saving={saving}
          onSave={() => saveField({ position: fieldEdit.val })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Position / Role</label>
            <input type="text" value={fieldEdit.val} onChange={e => setVal(e.target.value)}
              style={inputStyle} autoFocus placeholder="e.g. Site Manager" />
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'workplace' && (
        <SectionModal title="Edit Workplace" saving={saving}
          onSave={() => saveField({ workplaceId: fieldEdit.val || null })} onCancel={closeField}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Workplace</label>
            <select value={fieldEdit.val || ''} onChange={e => setVal(e.target.value || null)} style={inputStyle}>
              <option value="">— Not associated with a specific workplace —</option>
              {workplaces.map(w => (
                <option key={w.id} value={w.id}>{w.name}{w.isDefault ? ' (Default)' : ''}</option>
              ))}
            </select>
          </div>
        </SectionModal>
      )}
      {fieldEdit?.field === 'emails' && (
        <SectionModal title="Edit Emails" saving={saving}
          onSave={() => saveField({ emails: fieldEdit.val })} onCancel={closeField}>
          {fieldEdit.val.map(email => (
            <div key={email.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select value={email.label}
                onChange={e => setVal(fieldEdit.val.map(x => x.id === email.id ? { ...x, label: e.target.value } : x))}
                style={{ ...inputStyle, width: 100, flexShrink: 0 }}>
                {EMAIL_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <input type="email" value={email.value}
                onChange={e => setVal(fieldEdit.val.map(x => x.id === email.id ? { ...x, value: e.target.value } : x))}
                style={{ ...inputStyle, flex: 1 }} placeholder="email@example.com" />
              <DelBtn onClick={() => setVal(fieldEdit.val.filter(x => x.id !== email.id))} />
            </div>
          ))}
          <AddBtn onClick={() => setVal([...fieldEdit.val, newEmail()])} title="Add Email" />
        </SectionModal>
      )}
      {fieldEdit?.field === 'phones' && (
        <SectionModal title="Edit Phones" saving={saving}
          onSave={() => saveField({ phones: fieldEdit.val })} onCancel={closeField}>
          {fieldEdit.val.map(phone => (
            <div key={phone.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <PhoneCapture
                  code={phone.code} number={phone.number} label={phone.label}
                  onCodeChange={v => setVal(fieldEdit.val.map(x => x.id === phone.id ? { ...x, code: v } : x))}
                  onNumberChange={v => setVal(fieldEdit.val.map(x => x.id === phone.id ? { ...x, number: v } : x))}
                  onLabelChange={v => setVal(fieldEdit.val.map(x => x.id === phone.id ? { ...x, label: v } : x))}
                />
              </div>
              <DelBtn onClick={() => setVal(fieldEdit.val.filter(x => x.id !== phone.id))} />
            </div>
          ))}
          <AddBtn onClick={() => setVal([...fieldEdit.val, newPhone()])} title="Add Phone" />
        </SectionModal>
      )}
      {fieldEdit?.field === 'links' && (
        <SectionModal title="Edit Links" saving={saving}
          onSave={() => saveField({ links: fieldEdit.val })} onCancel={closeField}>
          {fieldEdit.val.map(link => (
            <div key={link.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input type="text" value={link.label}
                onChange={e => setVal(fieldEdit.val.map(x => x.id === link.id ? { ...x, label: e.target.value } : x))}
                style={{ ...inputStyle, width: 100, flexShrink: 0 }} placeholder="Label" />
              <input type="url" value={link.url}
                onChange={e => setVal(fieldEdit.val.map(x => x.id === link.id ? { ...x, url: e.target.value } : x))}
                style={{ ...inputStyle, flex: 1 }} placeholder="https://" />
              <DelBtn onClick={() => setVal(fieldEdit.val.filter(x => x.id !== link.id))} />
            </div>
          ))}
          <AddBtn onClick={() => setVal([...fieldEdit.val, newLink()])} title="Add Link" />
        </SectionModal>
      )}
    </>
  );
};

// ── Main page ──────────────────────────────────────────────

export const EmployerContacts = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [contacts,   setContacts]   = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // null | { mode:'add' } | { mode:'edit', contact }

  useEffect(() => {
    if (!activeOrg?.id || !activeProfile?.id) { setLoading(false); return; }
    Promise.all([
      orgAPI.getProfileContacts(activeOrg.id, activeProfile.id),
      orgAPI.getWorkplaces(activeOrg.id, activeProfile.id),
    ])
      .then(([cRes, wRes]) => {
        setContacts(cRes.data.data.contacts || []);
        setWorkplaces(wRes.data.data.workplaces || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile]);

  const workplaceMap = Object.fromEntries(workplaces.map((w) => [w.id, w.name]));

  const handleSave = async (form) => {
    if (modal?.mode === 'edit') {
      const { data } = await orgAPI.updateProfileContact(activeOrg.id, activeProfile.id, modal.contact.id, form);
      setContacts((prev) => prev.map((c) => c.id === modal.contact.id ? data.data.contact : c));
    } else {
      const { data } = await orgAPI.createProfileContact(activeOrg.id, activeProfile.id, form);
      setContacts((prev) => [...prev, data.data.contact].sort((a, b) => a.name.localeCompare(b.name)));
    }
    setModal(null);
  };

  const handleSaveField = async (contactId, patch) => {
    const contact = contacts.find(c => c.id === contactId);
    const { data } = await orgAPI.updateProfileContact(
      activeOrg.id, activeProfile.id, contactId, { ...contact, ...patch }
    );
    setContacts(prev => prev.map(c => c.id === contactId ? data.data.contact : c));
  };

  const handleDelete = async (contact) => {
    const ok = await confirm('Delete Contact', `Delete "${contact.name}"? This cannot be undone.`);
    if (!ok) return;
    await orgAPI.deleteProfileContact(activeOrg.id, activeProfile.id, contact.id);
    setContacts((prev) => prev.filter((c) => c.id !== contact.id));
  };

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>Contacts</h1>
          <p>People associated with this profile and their contact details.</p>
        </div>
        {isAdmin && (
          <AddBtn onClick={() => setModal({ mode: 'add' })} size={14} style={{ flexShrink: 0 }} title="Add Contact" />
        )}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: '48px 20px' }}>
            <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 16px' }}>
              No contacts added yet.
            </p>
            {isAdmin && (
              <AddBtn onClick={() => setModal({ mode: 'add' })} size={14} title="Add First Contact" />
            )}
          </div>
        </div>
      ) : (
        contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            workplaceName={contact.workplaceId ? workplaceMap[contact.workplaceId] : null}
            workplaces={workplaces}
            isAdmin={isAdmin}
            onEdit={() => setModal({ mode: 'edit', contact })}
            onDelete={() => handleDelete(contact)}
            onSaveField={handleSaveField}
          />
        ))
      )}

      {modal && (
        <ContactModal
          initial={modal.mode === 'edit' ? modal.contact : null}
          workplaces={workplaces}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {dialog}
    </div>
  );
};
